/**
 * Pure helper funkcije za UserProfilePage (bez React zavisnosti).
 * Re-eksportuje shared notification i entity access utilse; profile-specifične labele obaveza.
 */
import {
  getObligationStatusLabel as getSharedObligationStatusLabel,
  getObligationStatusClass as getSharedObligationStatusClass,
  OBLIGATION_STATUS_OPTIONS,
} from '@/shared/utils/domain/statusUtils';

/**
 * Deli login `organizations` na CSO (organizacije) i PB (institucije).
 * @param {{ verified?: object[], requested?: object[] }|null|undefined} raw - `loginData.organizations`.
 * @returns {{ organizations: { verified: object[], requested: object[] }, institutions: { verified: object[], requested: object[] } }}
 */
export function splitOrganizationsByType(raw) {
  const org = raw || { verified: [], requested: [] };
  const verified = Array.isArray(org.verified) ? org.verified : [];
  const requested = Array.isArray(org.requested) ? org.requested : [];
  const isCSO = (item) => (item.organization_type || '').toUpperCase() === 'CSO';
  const isPB = (item) => (item.organization_type || '').toUpperCase() === 'PB';
  return {
    organizations: { verified: verified.filter(isCSO), requested: requested.filter(isCSO) },
    institutions: { verified: verified.filter(isPB), requested: requested.filter(isPB) },
  };
}

export {
  getNotificationTypeLabel,
  getNotificationTriggerHint,
  resolveNotificationDisplay,
  notificationMatchesSearchQuery,
  buildNotificationContext,
  formatNotificationDate,
} from '@/shared/utils/notificationDisplay';

export {
  getOrganizationByEntityId,
  getObligationInPreparationById,
} from '@/shared/notifications/notificationResolvers';

export {
  getEntityAccessId,
  buildEntityAccessStatusMap,
  formatRequestAccessOptionLabel,
} from '@/shared/utils/entityAccessUtils';

/**
 * Labela statusa obaveze na profilu — uvek BE status obaveze (kao u adminu).
 * Faza AP-a ide u posebnu kolonu „Акциони план”.
 * @param {string|null|undefined} status - BE status obaveze.
 * @param {string|null|undefined} [_actionPlanStatus] - Zadržano radi kompatibilnosti API-ja kolona.
 * @returns {string} Lokalizovana labela.
 */
export function getObligationStatusLabel(status, _actionPlanStatus = null) {
  return getSharedObligationStatusLabel(status);
}

/**
 * CSS klasa za status chip obaveze na profilu.
 * @param {string|null|undefined} status - BE status obaveze.
 * @param {string|null|undefined} [_actionPlanStatus] - Zadržano radi kompatibilnosti API-ja kolona.
 * @returns {string} CSS klasa (npr. `status-obligation-draft`).
 */
export function getObligationStatusClass(status, _actionPlanStatus = null) {
  const normalizedStatus = String(status || '').toUpperCase();
  if (normalizedStatus === 'DRAFT') {
    return 'status-obligation-draft';
  }
  if (normalizedStatus === 'CREATED') {
    return 'status-obligation-created';
  }
  return getSharedObligationStatusClass(status);
}

/**
 * Nazivi odgovornih institucija iz BE `responsible_institutions[]`.
 * @param {object|null|undefined} obl - Obaveza sa BE.
 * @returns {string[]} Lista naziva institucija.
 */
export function getResponsibleInstitutionNames(obl) {
  const list = obl?.responsible_institutions;
  if (!Array.isArray(list) || list.length === 0) {
    const legacy = obl?.lead_institution || obl?.odgovorna_institucija || '';
    const trimmed = String(legacy || '').trim();
    return trimmed && trimmed !== '-' ? [trimmed] : [];
  }
  return list
    .map((entry) => {
      if (entry == null) return '';
      if (typeof entry === 'string') return entry.trim();
      return String(
        entry.institution_name ?? entry.name ?? entry.naziv ?? entry.organization_name ?? '',
      ).trim();
    })
    .filter(Boolean);
}

/**
 * Prikaz odgovornih institucija kao jedan string (zarezom odvojeno).
 * @param {object|null|undefined} obl - Obaveza sa BE.
 * @returns {string} Nazivi ili `-` ako nema.
 */
export function getInstitutionDisplay(obl) {
  const names = getResponsibleInstitutionNames(obl);
  if (names.length === 0) return '-';
  return names.join(', ');
}

/**
 * Imena moderatora iz BE `moderators[]` (moderator_first_name / moderator_last_name).
 * @param {object|null|undefined} obl - Obaveza sa BE.
 * @returns {string[]} Lista imena ili emailova.
 */
export function getObligationModeratorNames(obl) {
  const mods = obl?.moderators || obl?.moderator_list || [];
  if (!Array.isArray(mods)) return [];
  return mods
    .map((m) => {
      if (typeof m === 'string') return m.trim();
      const fromParts = [m.moderator_first_name ?? m.first_name, m.moderator_last_name ?? m.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      return (
        fromParts
        || m.name
        || m.full_name
        || m.moderator_email
        || m.email
        || m.userName
        || ''
      );
    })
    .filter(Boolean);
}

export {
  getActionPlanStatusLabel,
  getActionPlanStatusClass,
  ACTION_PLAN_STATUS_FILTER_VALUES,
  actionPlanStatusOptions,
} from '@/shared/utils/domain/actionPlanStatus';

/** Filter opcije statusa obaveza na profilu — iste labele kao u adminu. */
export const obligationsStatusOptions = OBLIGATION_STATUS_OPTIONS;

/** Podrazumevani filter statusa AP-a na tabu obaveza (DRAFT + IMPLEMENTATION). */
export const DEFAULT_ACTION_PLAN_STATUS_FILTER = Object.freeze(['DRAFT', 'IMPLEMENTATION']);

/**
 * Pronalazi opciju statusa za grid chip (boje i labela).
 * @param {string|null|undefined} status - BE status obaveze.
 * @returns {{ value: string, label: string, color: string, bgColor: string, borderColor: string }} Opcija za chip.
 */
export function getObligationStatusOption(status) {
  return (
    obligationsStatusOptions.find((o) => o.value === status) || {
      label: getObligationStatusLabel(status),
      color: '#6b7280',
      bgColor: '#e5e7eb',
      borderColor: '#d1d5db',
    }
  );
}

/**
 * AAL nivo autentifikacije za eID security modal.
 * @param {string|null|undefined} aalValue - AAL1 | AAL2 | AAL3.
 * @returns {{ label: string, name: string, description: string, badge: string }}
 */
export function getAALInfo(aalValue) {
  const levels = {
    AAL1: { label: 'AAL1', name: 'Основна аутентификација', description: 'Корисничко име и лозинка', badge: 'pp-status-basic' },
    AAL2: { label: 'AAL2', name: 'Вишефакторска аутентификација (MFA)', description: 'Лозинка + SMS код, апликација, токен', badge: 'pp-status-enabled' },
    AAL3: { label: 'AAL3', name: 'Највиши ниво аутентификације', description: 'Хардверски токен, сертификат или паметна картица', badge: 'pp-status-active' },
  };
  return levels[aalValue] || { label: aalValue, name: 'Непознат ниво', description: 'Ниво аутентификације није доступан', badge: 'pp-status-basic' };
}

/**
 * IAL nivo identiteta za eID security modal.
 * @param {string|null|undefined} ialValue - IAL1 | IAL2 | IAL3.
 * @returns {{ label: string, name: string, description: string, badge: string }}
 */
export function getIALInfo(ialValue) {
  const levels = {
    IAL1: { label: 'IAL1', name: 'Идентитет није верификован', description: 'Корисник се само пријавио (нпр. e-mail адресом)', badge: 'pp-status-basic' },
    IAL2: { label: 'IAL2', name: 'Идентитет је верификован', description: 'Потврђен на основу поузданог документа (лична карта, пасош, eID)', badge: 'pp-status-enabled' },
    IAL3: { label: 'IAL3', name: 'Идентитет је потврђен личним присуством', description: 'Висок ниво сигурности са вишеструком верификацијом', badge: 'pp-status-active' },
  };
  return levels[ialValue] || { label: ialValue, name: 'Непознат ниво', description: 'Ниво идентитета није доступан', badge: 'pp-status-basic' };
}

/**
 * Format datuma obaveze (sr-Latn-RS, dd.mm.yyyy).
 * @param {string|null|undefined} dateString - ISO ili parsabilan datum.
 * @returns {string} Formatiran datum ili `—`.
 */
export function formatObligationDate(dateString) {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
}

/**
 * Format datuma korisnika sa vremenom (sr-Latn-RS).
 * @param {string|null|undefined} dateString - ISO ili parsabilan datum.
 * @returns {string} Formatiran datum/vreme ili `—`.
 */
export function formatUserDate(dateString) {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validacija email adrese (regex).
 * @param {string|null|undefined} email - Email za proveru.
 * @returns {boolean} True ako je format validan.
 */
export function validateEmail(email) {
  return typeof email === 'string' && EMAIL_REGEX.test(email.trim());
}

/** Izvlači id entiteta iz login membership zapisa (CSO ili PB). */
export function getMembershipEntityId(entry) {
  if (entry == null) return null;
  const raw =
    entry.organization_id ??
    entry.organizationId ??
    entry.cso_id ??
    entry.org_id ??
    entry.institution_id ??
    entry.institutionId ??
    entry.organization?.id ??
    entry.cso?.id ??
    entry.id ??
    entry;
  if (raw == null) return null;
  if (typeof raw === 'object' && raw !== null) {
    return raw.id ?? raw.organization_id ?? raw.organizationId ?? raw.institution_id ?? null;
  }
  return raw;
}

function membershipIdMatches(entry, entityId) {
  const id = getMembershipEntityId(entry);
  if (id == null || entityId == null) return false;
  return String(id).trim() === String(entityId).trim();
}

/** Pronalazi verified membership za dati entity id. */
export function findVerifiedMembership(entityId, list) {
  const verified = Array.isArray(list?.verified) ? list.verified : [];
  return verified.find((entry) => membershipIdMatches(entry, entityId)) ?? null;
}

/** Da li korisnik ima verifikovan pristup entitetu (ne pending/requested). */
export function isVerifiedMembership(entityId, list) {
  return findVerifiedMembership(entityId, list) != null;
}

/**
 * Set ID-jeva admin-odobrenih entiteta (CSO LIST / public-body LIST).
 * @param {Array<{ id?: string, organization_id?: string }>|null|undefined} entities
 * @returns {Set<string>}
 */
export function buildApprovedEntityIdSet(entities) {
  const set = new Set();
  if (!Array.isArray(entities)) return set;
  entities.forEach((entity) => {
    const id = entity?.id ?? entity?.organization_id ?? entity?.organizationId ?? entity?.institution_id;
    if (id != null && String(id).trim()) set.add(String(id).trim());
  });
  return set;
}

/** Da li je entitet odobren od strane admina (organization.valid_from). */
export function isApprovedEntity(entityId, approvedEntityIds) {
  if (entityId == null || !approvedEntityIds || approvedEntityIds.size === 0) return false;
  return approvedEntityIds.has(String(entityId).trim());
}

/**
 * Da li korisnik može izabrati entitet u settings tabu:
 * aktivno članstvo (login verified) + admin-odobren entitet.
 */
export function isSelectableMembership(entityId, list, approvedEntityIds) {
  return isVerifiedMembership(entityId, list) && isApprovedEntity(entityId, approvedEntityIds);
}

/** Da li je članstvo aktivno, ali entitet još čeka admin verifikaciju. */
export function isPendingEntityVerification(entityId, list, approvedEntityIds) {
  return isVerifiedMembership(entityId, list) && !isApprovedEntity(entityId, approvedEntityIds);
}

/** Prikazno ime iz verified membership zapisa. */
export function getVerifiedMembershipName(entry) {
  if (entry == null) return null;
  const n =
    entry.organization_name ??
    entry.organizationName ??
    entry.institution_name ??
    entry.institutionName ??
    entry.organization?.name ??
    entry.cso?.name ??
    entry.name ??
    entry.naziv;
  return typeof n === 'string' && n.trim() ? n.trim() : null;
}
